import classNames from 'classnames';
import { debounce } from 'lodash';
import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import { deleteSurfaceRequest, duplicateSurface, setSurfaceSettings } from 'containers/Surfaces/actions';
import { intlGet } from 'utils/intlGet';
import styles from '../../styles.module.scss';

type Props = {
  screenId: string;
  dataTestId: string;
  toggleTooltip: (element?: React.RefObject<HTMLElement>, show?: boolean, text?: string) => void;
  isDeletingDisabled: boolean;
  allowScreenSettings: boolean;
  isReadOnly: boolean | undefined;
};

export const ScreenMenu: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const [isScreenMenuShown, setIsScreenMenuShown] = useState(false);
  const deleteIconRef = useRef<HTMLDivElement>(null);
  const copyIconRef = useRef<HTMLDivElement>(null);
  const settingsIconRef = useRef<HTMLDivElement>(null);

  const {
    screenId,
    dataTestId,
    toggleTooltip,
    isDeletingDisabled,
    allowScreenSettings,
    isReadOnly,
  } = props;

  const onDeleteScreen = (): void => {
    dispatch(deleteSurfaceRequest(screenId));
  };

  const onDuplicateScreen = (): void => {
    dispatch(duplicateSurface(screenId));
  };

  const onScreenSettings = (): void => {
    dispatch(setSurfaceSettings(screenId));
  };

  const showScreenMenu = debounce(() => setIsScreenMenuShown(true), 100);
  const hideScreenMenu = debounce(() => setIsScreenMenuShown(false), 1000);

  const onMouseEnterMenu = (): void => {
    hideScreenMenu.cancel();
    showScreenMenu();
  };

  const onMouseLeaveMenu = (): void => {
    showScreenMenu.cancel();
    hideScreenMenu();
  };

  if (isReadOnly) {
    return null;
  }

  return (
    <div className={classNames(styles.screenMenu, { [styles.screenMenu__editMode]: isScreenMenuShown })}
      data-testid={`${dataTestId}-info-description-title-screenMenu`}
    >
      <div className={styles.screenMenuIcons} onMouseEnter={onMouseEnterMenu}
        onMouseLeave={onMouseLeaveMenu}
        data-testid={`${dataTestId}-info-description-title-screenMenu-icons`}
      >
        {
          !isScreenMenuShown ?
            <div className={styles.iconContainer} data-testid={`${dataTestId}-info-description-title-screenMenu-icons-menu`}>
              <Icon type={IconType.MENU} size="sm" color="grey" />
            </div>
            :
            <div className={styles.screenMenu__expanded} data-testid={`${dataTestId}-info-description-title-screenMenu-icons-editMode`}>
              <div
                className={styles.iconContainer}
                ref={deleteIconRef}
                onMouseEnter={(): void => toggleTooltip(deleteIconRef, true, intlGet('ScreensPanel', 'Delete'))}
                onMouseLeave={(): void => toggleTooltip()}
                data-testid={`${dataTestId}-info-description-title-screenMenu-icons-editMode-icon0`}
              >
                <Icon
                  type={IconType.DELETE_BIN}
                  size="sm"
                  color="grey"
                  onClick={onDeleteScreen}
                  isDisabled={isDeletingDisabled}
                />
              </div>
              <div
                className={styles.iconContainer}
                ref={copyIconRef}
                onMouseEnter={(): void => toggleTooltip(copyIconRef, true, intlGet('ScreensPanel', 'Duplicate'))}
                onMouseLeave={(): void => toggleTooltip()}
                data-testid={`${dataTestId}-info-description-title-screenMenu-icons-editMode-icon1`}
              >
                <Icon type={IconType.CONTENT_COPY} size="sm" color="grey" onClick={onDuplicateScreen} />
              </div>
              { allowScreenSettings && <div
                className={styles.iconContainer}
                ref={settingsIconRef}
                onMouseOver={(): void => toggleTooltip(settingsIconRef, true, intlGet('ScreensPanel', 'Settings'))}
                onMouseLeave={(): void => toggleTooltip()}
                data-testid={`${dataTestId}-info-description-title-screenMenu-icons-editMode-icon3`}
              >
                <Icon type={IconType.GEAR} size="sm" color="grey" onClick={onScreenSettings} />
              </div>
              }
            </div>
        }
      </div>
    </div>
  );
};

